import { cpf, cnpj } from "cpf-cnpj-validator";

export const isPJ = function (value) {
  return value.trim().toUpperCase() === "PJ";
};

export const doesNotHaveValue = function (input) {
  return (input?.value.trim() ?? "") === "";
};

/**
 * @param {HTMLElement} el to transform
 * @param {boolean} isHidden
 */
export const setElementHiddenClass = (el, isHidden) => {
  el.classList.toggle("hidden", isHidden);
};

/**
 * @param {HTMLButtonElement} button to transform
 * @param {boolean} isInvalid
 */
export const disableSubmitButtonElement = (button, isInvalid) => {
  button.disabled = isInvalid;
  button.classList.toggle("cursor-not-allowed", isInvalid);
  button.classList.toggle("opacity-60", isInvalid);
};

/**
 * @param {HTMLInputElement} input to transform
 * @param {boolean} isDisabled
 */
export const disableInputField = (input, isDisabled, clearValue = true) => {
  if (clearValue) {
    input.value = "";
  }
  input.disabled = isDisabled;
  input.style = isDisabled ? "background-color: #e0e0e0;" : "";
};

/**
 * @param {HTMLInputElement} input to transform
 * @param {boolean} isDisabledAndNotRequired
 */
export const disableRequiredInputField = (input, isDisabledAndNotRequired, clear = true) => {
  disableInputField(input, isDisabledAndNotRequired, clear);
  input.required = !isDisabledAndNotRequired;
};

export const checkoutCustomerFormHelpers = (elements) => {
  const {
    alertMessageDocumentElement,
    documentLabelElement,
    formDocumentElement,
    formPhoneNumberElement,
    formClientType,
    isForeignerElement,
    submitButtonElement,
    whatsappAcceptElement,
    whatsappLabelElement
  } = elements;

  function toggleDocumentErrorMessage (isInvalid, isEmpty) {
    const emptyErrorMessage = alertMessageDocumentElement.querySelector(".document_empty");
    const invalidErrorMessage = alertMessageDocumentElement.querySelector(".document_invalid");

    if (isEmpty) {
      setElementHiddenClass(emptyErrorMessage, false);
      setElementHiddenClass(invalidErrorMessage, true);
    } else if (isInvalid) {
      setElementHiddenClass(emptyErrorMessage, true);
      setElementHiddenClass(invalidErrorMessage, false);
    }

    setElementHiddenClass(alertMessageDocumentElement, !isInvalid);

    disableSubmitButtonElement(submitButtonElement, isInvalid);
  }

  const pjMode = isPJ(formClientType.value);

  const helpers = {
    pjMode,
    /**
     *
     * @param {HTMLInputValue} formClientType
     * @returns boolean
     */
    cpfCnpjValidator () {
      if (isForeignerElement != true && isForeignerElement?.checked) {
        return true;
      }
      const docNum = formDocumentElement.value?.trim() ?? "";
      const docNumDigits = docNum.split("").filter(digit => /^\d$/.test(digit));
      const isFilled = docNumDigits.length > 0;
      const isValid = isFilled && (pjMode ? cnpj.isValid(docNum) : cpf.isValid(docNum));

      if (formDocumentElement.required) {
        toggleDocumentErrorMessage(!isValid, !isFilled);
        return isValid;
      } else {
        const valid = isFilled ? isValid : true;
        toggleDocumentErrorMessage(!valid, false);
        return valid;
      }
    },
    /** @param {boolean} state */
    disableCpfCnpjFields: isForeignerElement
      ? (state) => {
          const optionalLabel = documentLabelElement.querySelector("small");
          if (!optionalLabel || (optionalLabel && optionalLabel.classList.contains("inline-zero-width"))) {
            documentLabelElement.classList.toggle("required", !state);
          }
          disableRequiredInputField(formDocumentElement, state);

          if (state) {
            toggleDocumentErrorMessage(false, false);
          }
        }
      : undefined,
    phoneNumberChangeHandler () {
      const phoneValue = formPhoneNumberElement
        .value
        .split("")
        .filter(num => /\d/.test(num));

      const valid = phoneValue.length > 6;

      whatsappAcceptElement.checked = valid;
      whatsappAcceptElement.classList.toggle("hidden", !valid);
      whatsappLabelElement.classList.toggle("hidden", !valid);
    }
  };
  // to validate cpf/cnpj
  formDocumentElement.addEventListener("blur", () => {
    helpers.cpfCnpjValidator(formClientType);
  });

  // to disabled cpf/cnpj fields
  if (isForeignerElement && isForeignerElement !== true) {
    isForeignerElement.addEventListener("change", (e) => {
      helpers.disableCpfCnpjFields(e.target.checked);
    });
  }

  // Show hidden whatsapp checkbox and label
  formPhoneNumberElement.addEventListener("keyup", helpers.phoneNumberChangeHandler);

  return helpers;
};

export const parseFetchResponse = (response) =>
  response
    .json()
    .then((data) => ({ status: response.status, body: data }));

/**
 * Transform object to inline CSS string to use directly in style properties.
 *
 * @param {Object.<string, string | number>} obj containing css key:values
 * @returns
 */
export const objectToInlineCss = (obj) => Object.entries(obj).map(([key, value]) => `${key}: ${value}`).join(";");

/**
 *
 * @param {string} query to search element
 * @returns {HTMLElement} element
 */
export const querySelectorOrFail = (query) => {
  const el = document.querySelector(query);
  if (!el) {
    throw new Error("Not element found with query " + query);
  }
  return el;
};

/**
 * Trigger a blur (loss of focus) in element and document's current active element when Enter is pressed.
 * @param {KeyboardEvent} event
 */
export function blurOnEnterKeyupHandler (event) {
  if (event.key === "Enter") {
    event.target.blur();
    document.activeElement.blur();
  }
}

/**
 * Forces blur (loss of focus) in the active document on event.
 */
export function forceBlurOnEventHandler () {
  document.activeElement.blur();
}
